import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { COUNTRIES_WITH_FLAGS, CountryData } from 'constant/countries-with-flags';
import { useTranslation } from 'react-i18next';

/**
 * CountrySelect Component
 *
 * A searchable country selection dropdown component with flag emojis.
 * Built on top of Radix UI Popover and Command components for better search UX.
 *
 * Features:
 * - Searchable country list with real-time filtering
 * - Flag emoji displayed next to each country name
 * - Keyboard navigation support
 * - Accessible with proper ARIA attributes
 * - Theme-aware styling
 * - Integrates with React Hook Form
 *
 * @param {string} value - Currently selected country name
 * @param {(value: string) => void} onChange - Callback when country is selected
 * @param {string} [placeholder] - Placeholder text when no country is selected
 * @param {string} [className] - Additional CSS classes for the trigger button
 * @param {boolean} [disabled] - Whether the select is disabled
 *
 * @returns {JSX.Element} A searchable country select dropdown
 *
 * @example
 * // Basic usage
 * <CountrySelect
 *   value={selectedCountry}
 *   onChange={setSelectedCountry}
 *   placeholder="Select country"
 * />
 *
 * // With React Hook Form
 * <FormField
 *   control={form.control}
 *   name="country"
 *   render={({ field }) => (
 *     <FormItem>
 *       <FormLabel>Country</FormLabel>
 *       <CountrySelect
 *         value={field.value}
 *         onChange={field.onChange}
 *         placeholder="Select your country"
 *       />
 *       <FormMessage />
 *     </FormItem>
 *   )}
 * />
 */

interface CountrySelectProps {
    value?: string;
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
}

export function CountrySelect({
    value,
    onChange,
    placeholder = 'Select country...',
    className,
    disabled = false,
}: CountrySelectProps) {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState('');

    // Find the selected country data
    const selectedCountry = React.useMemo(() => {
        return COUNTRIES_WITH_FLAGS.find((country) => country.name === value);
    }, [value]);

    // Filter countries based on search query
    const filteredCountries = React.useMemo(() => {
        if (!searchQuery) return COUNTRIES_WITH_FLAGS;

        const query = searchQuery.toLowerCase();
        return COUNTRIES_WITH_FLAGS.filter(
            (country) =>
                country.name.toLowerCase().includes(query) ||
                country.code.toLowerCase().includes(query)
        );
    }, [searchQuery]);

    const handleSelect = (country: CountryData) => {
        onChange(country.name);
        setOpen(false);
        setSearchQuery('');
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        'w-full justify-between h-11 font-normal',
                        !value && 'text-muted-foreground',
                        className
                    )}
                    disabled={disabled}
                >
                    {selectedCountry ? (
                        <div className="flex items-center gap-2">
                            <span className="text-lg">{selectedCountry.flag}</span>
                            <span>{selectedCountry.name}</span>
                        </div>
                    ) : (
                        placeholder
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
                <Command shouldFilter={false}>
                    <CommandInput
                        placeholder="Search country..."
                        value={searchQuery}
                        onValueChange={setSearchQuery}
                    />
                    <CommandList>
                        <CommandEmpty>{t('NO_COUNTRY_FOUND', { defaultValue: 'No country found.' })}</CommandEmpty>
                        <CommandGroup>
                            {filteredCountries.map((country) => (
                                <CommandItem
                                    key={country.code}
                                    value={country.name}
                                    onSelect={() => handleSelect(country)}
                                    className="cursor-pointer"
                                >
                                    <div className="flex items-center gap-2 flex-1">
                                        <span className="text-lg">{country.flag}</span>
                                        <span>{country.name}</span>
                                    </div>
                                    <Check
                                        className={cn(
                                            'ml-auto h-4 w-4',
                                            value === country.name ? 'opacity-100' : 'opacity-0'
                                        )}
                                    />
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

